/**
 * Authentication API Service
 * Handles communication with Django backend for user authentication
 */

import { DATA_SOURCE_CONFIG, API_ENDPOINTS } from '../config/appConfig';
import ConfigService from './configService';

// Get the base URL for the API (using ConfigService for consistency)
const getBaseUrl = () => {
  return ConfigService.getServerUrl();
};

const getApiUrl = (endpoint) => {
  return ConfigService.getApiUrl(endpoint);
};

// Get stored auth token
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

// Get auth headers
export const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }
  return headers;
};

/**
 * Login user with backend API
 */
export const loginApi = async (username, password) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.LOGIN), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (response.ok) {
      setAuthToken(data.token);
      return {
        success: true,
        user: data.user,
        token: data.token,
        message: '登录成功'
      };
    } else {
      return {
        success: false,
        message: data.non_field_errors?.[0] || data.detail || '用户名或密码错误'
      };
    }
  } catch (error) {
    console.error('Login API error:', error);
    return {
      success: false,
      message: '网络错误，请检查服务器连接'
    };
  }
};

/**
 * Login or create user with phone SMS verification.
 */
export const smsLoginApi = async ({ phoneNumber, verificationCode, username }) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.SMS_LOGIN), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
        verification_code: verificationCode,
        username: username || '',
        name: username || '',
      }),
    });

    const data = await response.json();

    if (response.ok) {
      setAuthToken(data.token);
      return {
        success: true,
        user: data.user,
        token: data.token,
        isNewUser: Boolean(data.is_new_user),
        message: data.is_new_user ? '账号已创建' : '登录成功'
      };
    }

    return {
      success: false,
      needsUsername: Boolean(data.needs_username),
      message: data.username?.[0] || data.verification_code?.[0] || data.phone_number?.[0] || data.detail || '登录失败'
    };
  } catch (error) {
    console.error('SMS login API error:', error);
    return {
      success: false,
      message: '网络错误，请检查服务器连接'
    };
  }
};

/**
 * Send SMS verification code with backend API
 */
export const sendSmsVerificationApi = async (phoneNumber) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.SEND_SMS_CODE), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone_number: phoneNumber }),
    });

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        message: data.message || '验证码已发送'
      };
    }

    return {
      success: false,
      message: data.phone_number?.[0] || data.detail || '验证码发送失败'
    };
  } catch (error) {
    console.error('Send SMS API error:', error);
    return {
      success: false,
      message: '网络错误，请检查服务器连接'
    };
  }
};

/**
 * Register new user with backend API
 */
export const registerApi = async (userData) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.REGISTER), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: userData.username || userData.name,
        email: userData.email || '',
        phone_number: userData.phoneNumber,
        verification_code: userData.verificationCode,
        password: userData.password,
        password_confirm: userData.confirmPassword || userData.password,
        name: userData.name,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      setAuthToken(data.token);
      return {
        success: true,
        user: data.user,
        token: data.token,
        message: '注册成功'
      };
    } else {
      // Handle various error formats from Django
      let errorMessage = '注册失败';
      if (data.username) {
        errorMessage = `用户名: ${data.username[0]}`;
      } else if (data.name) {
        errorMessage = `姓名: ${data.name[0]}`;
      } else if (data.phone_number) {
        errorMessage = `手机号: ${data.phone_number[0]}`;
      } else if (data.verification_code) {
        errorMessage = `验证码: ${data.verification_code[0]}`;
      } else if (data.email) {
        errorMessage = `邮箱: ${data.email[0]}`;
      } else if (data.password) {
        errorMessage = `密码: ${data.password[0]}`;
      } else if (data.non_field_errors) {
        errorMessage = data.non_field_errors[0];
      } else if (data.detail) {
        errorMessage = data.detail;
      }
      return {
        success: false,
        message: errorMessage
      };
    }
  } catch (error) {
    console.error('Register API error:', error);
    return {
      success: false,
      message: '网络错误，请检查服务器连接'
    };
  }
};

/**
 * Get WeChat login availability.
 */
export const getWeChatLoginConfigApi = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.WECHAT_CONFIG), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return {
      success: response.ok,
      enabled: Boolean(data.enabled),
      mode: data.mode,
      message: data.detail || '',
    };
  } catch (error) {
    console.error('WeChat config API error:', error);
    return {
      success: false,
      enabled: false,
      message: '微信登录配置读取失败',
    };
  }
};

/**
 * Build the backend WeChat login start URL.
 */
export const getWeChatLoginUrl = (nextPath = '/') => {
  const next = nextPath && nextPath.startsWith('/') ? nextPath : '/';
  return `${getApiUrl(API_ENDPOINTS.AUTH.WECHAT_LOGIN)}?next=${encodeURIComponent(next)}`;
};

/**
 * Store a successful WeChat login response returned by the backend callback.
 */
export const setWeChatLoginSession = (token, user) => {
  setAuthToken(token);
  localStorage.setItem('yogaUser', JSON.stringify(user));
};

/**
 * Logout user
 */
export const logoutApi = async () => {
  try {
    const token = getAuthToken();
    if (token) {
      await fetch(getApiUrl(API_ENDPOINTS.AUTH.LOGOUT), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    }
  } catch (error) {
    console.error('Logout API error:', error);
  } finally {
    setAuthToken(null);
  }
  return { success: true, message: '已登出' };
};

export default {
  loginApi,
  smsLoginApi,
  sendSmsVerificationApi,
  registerApi,
  getWeChatLoginConfigApi,
  getWeChatLoginUrl,
  setWeChatLoginSession,
  logoutApi,
  getAuthToken,
  setAuthToken,
  getAuthHeaders,
};
